<template>
  <div class="wallet-metadata-page">
    <div class="page-header">
      <div class="page-title-block">
        <h1 class="page-title">钱包元数据管理</h1>
        <p class="page-subtitle">维护千川钱包元数据表，支持新增、修改、删除</p>
      </div>
      <div class="header-actions">
        <button type="button" class="ghost-btn" @click="goBack">返回余额管理</button>
        <button type="button" class="ghost-btn" :disabled="loading" @click="fetchList">
          {{ loading ? '加载中...' : '刷新列表' }}
        </button>
        <button type="button" class="ghost-btn" @click="openTransferModal">手动转账</button>
        <button type="button" class="primary-btn" @click="openAddModal">新增元数据</button>
      </div>
    </div>

    <div v-if="statusMessage" class="status-banner" :class="statusType">
      {{ statusMessage }}
    </div>

    <div class="content-card">
      <div v-if="loading" class="placeholder-state">正在加载元数据列表...</div>
      <div v-else-if="list.length === 0" class="placeholder-state">暂无元数据，点击右上角“新增元数据”开始维护</div>
      <div v-else class="table-wrapper">
        <table class="metadata-table">
          <thead>
            <tr>
              <th>店铺ID</th>
              <th>店铺名称</th>
              <th>组织ID</th>
              <th>锚定账户ID</th>
              <th>锚定账户名称</th>
              <th>创建时间</th>
              <th class="action-column">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in list" :key="item.id">
              <td>{{ item.username || '-' }}</td>
              <td>{{ item.shopName || '-' }}</td>
              <td>{{ item.organizationId || '-' }}</td>
              <td>{{ item.opponentTargetId || '-' }}</td>
              <td>{{ item.opponentTargetName || '-' }}</td>
              <td>{{ item.createTime || '-' }}</td>
              <td class="table-actions">
                <button type="button" class="mini-btn edit-btn" @click="openEditModal(item)">修改</button>
                <button type="button" class="mini-btn delete-btn" :disabled="deletingId === item.id" @click="deleteItem(item)">
                  {{ deletingId === item.id ? '删除中...' : '删除' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="showModal" class="modal-mask" @click.self="closeModal">
      <div class="modal-card">
        <div class="modal-header">
          <h3>{{ editingId ? '修改钱包元数据' : '新增钱包元数据' }}</h3>
          <button type="button" class="modal-close" @click="closeModal">关闭</button>
        </div>

        <div class="modal-body">
          <div class="form-grid">
            <div class="form-item">
              <label for="metadata-username">店铺ID</label>
              <input
                id="metadata-username"
                v-model.trim="form.username"
                type="text"
                maxlength="255"
                placeholder="请输入店铺ID"
              />
            </div>
            <div class="form-item">
              <label for="metadata-shop-name">店铺名称</label>
              <input
                id="metadata-shop-name"
                v-model.trim="form.shopName"
                type="text"
                maxlength="255"
                placeholder="请输入店铺名称"
              />
            </div>
            <div class="form-item">
              <label for="metadata-organization-id">组织ID</label>
              <input
                id="metadata-organization-id"
                v-model.trim="form.organizationId"
                type="text"
                maxlength="20"
                placeholder="请输入组织ID"
              />
            </div>
            <div class="form-item">
              <label for="metadata-opponent-target-id">锚定账户ID</label>
              <input
                id="metadata-opponent-target-id"
                v-model.trim="form.opponentTargetId"
                type="text"
                maxlength="20"
                placeholder="请输入锚定账户ID"
              />
            </div>
            <div class="form-item form-item-full">
              <label for="metadata-opponent-target-name">锚定账户名称</label>
              <input
                id="metadata-opponent-target-name"
                v-model.trim="form.opponentTargetName"
                type="text"
                maxlength="255"
                placeholder="请输入锚定账户名称"
              />
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="ghost-btn light" :disabled="submitting" @click="closeModal">取消</button>
          <button type="button" class="primary-btn" :disabled="submitting" @click="submitForm">
            {{ submitting ? '保存中...' : (editingId ? '保存修改' : '确认新增') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showTransferModal" class="modal-mask" @click.self="closeTransferModal">
      <div class="modal-card">
        <div class="modal-header">
          <h3>手动账户转账</h3>
          <button type="button" class="modal-close" @click="closeTransferModal">关闭</button>
        </div>

        <div class="modal-body">
          <div class="form-grid">
            <div class="form-item form-item-full">
              <label for="transfer-wallet-source">关联元数据</label>
              <select
                id="transfer-wallet-source"
                v-model="transferForm.walletSourceId"
                class="form-select"
                @change="handleTransferWalletSourceChange"
              >
                <option value="">请选择元数据记录</option>
                <option v-for="item in list" :key="item.id" :value="String(item.id)">
                  {{ `${item.shopName || '-'} / ${item.username || '-'} / ${item.opponentTargetName || '-'}` }}
                </option>
              </select>
            </div>
            <div class="form-item">
              <label for="transfer-username">店铺ID</label>
              <input
                id="transfer-username"
                v-model.trim="transferForm.username"
                type="text"
                maxlength="255"
                placeholder="请输入店铺ID"
              />
            </div>
            <div class="form-item">
              <label for="transfer-organization-id">组织ID</label>
              <input
                id="transfer-organization-id"
                v-model.trim="transferForm.organizationId"
                type="text"
                maxlength="20"
                placeholder="请输入组织ID"
              />
            </div>
            <div class="form-item">
              <label for="transfer-opponent-target-id">锚定账户ID</label>
              <input
                id="transfer-opponent-target-id"
                v-model.trim="transferForm.opponentTargetId"
                type="text"
                maxlength="20"
                placeholder="请输入锚定账户ID"
              />
            </div>
            <div class="form-item">
              <label for="transfer-target-id">目标账户ID</label>
              <input
                id="transfer-target-id"
                v-model.trim="transferForm.targetId"
                type="text"
                maxlength="20"
                placeholder="请输入目标账户ID"
              />
            </div>
            <div class="form-item form-item-full">
              <label for="transfer-amount">转账金额(元)</label>
              <input
                id="transfer-amount"
                v-model.trim="transferForm.transferAmount"
                type="text"
                maxlength="20"
                placeholder="请输入转账金额，最多两位小数"
              />
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="ghost-btn light" :disabled="transferSubmitting" @click="closeTransferModal">取消</button>
          <button type="button" class="primary-btn" :disabled="transferSubmitting" @click="submitTransfer">
            {{ transferSubmitting ? '转账中...' : '确认转账' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import API_CONFIG from '@/api/config'
import { createAdaptiveViewportMixin } from '@/mixins/adaptiveViewport'

export default {
  name: 'WalletMetadataManagement',
  mixins: [createAdaptiveViewportMixin({ designWidth: 1440, designHeight: 900, minScale: 0.6 })],
  data() {
    return {
      cardNumber: '',
      loading: false,
      submitting: false,
      transferSubmitting: false,
      deletingId: null,
      showModal: false,
      showTransferModal: false,
      editingId: null,
      statusMessage: '',
      statusType: 'success',
      list: [],
      form: this.getDefaultForm(),
      transferForm: this.getDefaultTransferForm()
    }
  },
  created() {
    this.cardNumber = this.$route.query.cardNumber || localStorage.getItem('cardNumber') || localStorage.getItem('username') || ''
    this.fetchList()
  },
  methods: {
    getDefaultForm() {
      return {
        username: '',
        shopName: '',
        organizationId: '',
        opponentTargetId: '',
        opponentTargetName: ''
      }
    },
    getDefaultTransferForm() {
      return {
        walletSourceId: '',
        username: '',
        organizationId: '',
        opponentTargetId: '',
        targetId: '',
        transferAmount: ''
      }
    },
    showStatus(message, type = 'success') {
      this.statusMessage = message
      this.statusType = type
    },
    toSafeHeaderValue(value) {
      const str = String(value ?? '')
      for (let i = 0; i < str.length; i += 1) {
        if (str.charCodeAt(i) > 255) {
          return encodeURIComponent(str)
        }
      }
      return str
    },
    getRequestHeaders() {
      const token = localStorage.getItem('authToken')
      const safeToken = this.toSafeHeaderValue(token)
      const cardValue = this.toSafeHeaderValue(this.cardNumber || '')
      return {
        ...(token ? { Authorization: `Bearer ${safeToken}` } : {}),
        ...(cardValue ? { cardnumber: cardValue, cardNumber: cardValue } : {})
      }
    },
    normalizeList(rows) {
      return (rows || []).map(item => ({
        id: item.id,
        username: item.username,
        shopName: item.shopName ?? item.shop_name,
        organizationId: item.organizationId ?? item.organization_id,
        opponentTargetId: item.opponentTargetId ?? item.opponent_target_id,
        opponentTargetName: item.opponentTargetName ?? item.opponent_target_name,
        createTime: item.createTime ?? item.create_time
      }))
    },
    async fetchList() {
      this.loading = true
      this.statusMessage = ''
      try {
        const response = await axios.get(API_CONFIG.walletSourceList(), {
          headers: this.getRequestHeaders()
        })
        if (Number(response?.data?.code) === 200) {
          const rows = response?.data?.data || response?.data?.rows || []
          this.list = this.normalizeList(rows)
          return
        }
        this.list = []
        this.showStatus(response?.data?.msg || '获取钱包元数据失败', 'error')
      } catch (error) {
        this.list = []
        this.showStatus(error?.response?.data?.msg || '获取钱包元数据失败，请稍后重试', 'error')
      } finally {
        this.loading = false
      }
    },
    openAddModal() {
      this.editingId = null
      this.form = this.getDefaultForm()
      this.showModal = true
      this.statusMessage = ''
    },
    openEditModal(item) {
      this.editingId = item.id
      this.form = {
        username: item.username || '',
        shopName: item.shopName || '',
        organizationId: item.organizationId !== null && item.organizationId !== undefined ? String(item.organizationId) : '',
        opponentTargetId: item.opponentTargetId !== null && item.opponentTargetId !== undefined ? String(item.opponentTargetId) : '',
        opponentTargetName: item.opponentTargetName || ''
      }
      this.showModal = true
      this.statusMessage = ''
    },
    closeModal() {
      if (this.submitting) {
        return
      }
      this.showModal = false
    },
    openTransferModal() {
      this.transferForm = this.getDefaultTransferForm()
      this.showTransferModal = true
      this.statusMessage = ''
    },
    closeTransferModal() {
      if (this.transferSubmitting) {
        return
      }
      this.showTransferModal = false
    },
    handleTransferWalletSourceChange() {
      const selectedId = Number(this.transferForm.walletSourceId)
      const selectedItem = this.list.find(item => item.id === selectedId)
      if (!selectedItem) {
        this.transferForm.username = ''
        this.transferForm.organizationId = ''
        this.transferForm.opponentTargetId = ''
        return
      }
      this.transferForm.username = selectedItem.username || ''
      this.transferForm.organizationId = selectedItem.organizationId !== null && selectedItem.organizationId !== undefined ? String(selectedItem.organizationId) : ''
      this.transferForm.opponentTargetId = ''
    },
    validateForm() {
      const requiredFields = ['username', 'shopName', 'organizationId', 'opponentTargetId', 'opponentTargetName']
      const hasEmptyField = requiredFields.some(key => !String(this.form[key] || '').trim())
      if (hasEmptyField) {
        this.showStatus('请完整填写元数据信息', 'error')
        return false
      }
      const numericPattern = /^\d+$/
      if (!numericPattern.test(this.form.organizationId) || !numericPattern.test(this.form.opponentTargetId)) {
        this.showStatus('组织ID和锚定账户ID必须为数字', 'error')
        return false
      }
      return true
    },
    validateTransferForm() {
      const requiredFields = ['username', 'organizationId', 'opponentTargetId', 'targetId', 'transferAmount']
      const hasEmptyField = requiredFields.some(key => !String(this.transferForm[key] || '').trim())
      if (hasEmptyField) {
        this.showStatus('请完整填写手动转账参数', 'error')
        return false
      }
      const numericPattern = /^\d+$/
      if (!numericPattern.test(this.transferForm.organizationId)
        || !numericPattern.test(this.transferForm.opponentTargetId)
        || !numericPattern.test(this.transferForm.targetId)) {
        this.showStatus('组织ID、锚定账户ID、目标账户ID必须为数字', 'error')
        return false
      }
      if (!/^\d+(\.\d{1,2})?$/.test(this.transferForm.transferAmount)) {
        this.showStatus('转账金额格式不正确，请输入最多两位小数的元金额', 'error')
        return false
      }
      return true
    },
    async submitForm() {
      if (!this.validateForm()) {
        return
      }
      this.submitting = true
      this.statusMessage = ''
      const payload = {
        username: this.form.username,
        shopName: this.form.shopName,
        organizationId: Number(this.form.organizationId),
        opponentTargetId: Number(this.form.opponentTargetId),
        opponentTargetName: this.form.opponentTargetName
      }
      if (this.editingId) {
        payload.id = Number(this.editingId)
      }

      try {
        const response = this.editingId
          ? await axios.put(API_CONFIG.walletSource(), payload, {
              headers: {
                ...this.getRequestHeaders(),
                'Content-Type': 'application/json'
              }
            })
          : await axios.post(API_CONFIG.walletSource(), payload, {
              headers: {
                ...this.getRequestHeaders(),
                'Content-Type': 'application/json'
              }
            })

        if (Number(response?.data?.code) === 200) {
          this.showModal = false
          this.showStatus(this.editingId ? '钱包元数据修改成功' : '钱包元数据新增成功')
          this.editingId = null
          this.form = this.getDefaultForm()
          await this.fetchList()
          return
        }
        this.showStatus(response?.data?.msg || (this.editingId ? '钱包元数据修改失败' : '钱包元数据新增失败'), 'error')
      } catch (error) {
        this.showStatus(error?.response?.data?.msg || (this.editingId ? '钱包元数据修改失败，请稍后重试' : '钱包元数据新增失败，请稍后重试'), 'error')
      } finally {
        this.submitting = false
      }
    },
    async submitTransfer() {
      if (!this.validateTransferForm()) {
        return
      }
      this.transferSubmitting = true
      this.statusMessage = ''
      try {
        const response = await axios.post(API_CONFIG.walletSourceManualTransfer(), {
          username: this.transferForm.username,
          organizationId: Number(this.transferForm.organizationId),
          opponentTargetId: Number(this.transferForm.opponentTargetId),
          targetId: Number(this.transferForm.targetId),
          transferAmount: this.transferForm.transferAmount
        }, {
          headers: {
            ...this.getRequestHeaders(),
            'Content-Type': 'application/json'
          }
        })
        if (Number(response?.data?.code) === 200) {
          this.showTransferModal = false
          this.transferForm = this.getDefaultTransferForm()
          this.showStatus(response?.data?.msg || '手动转账成功')
          return
        }
        this.showStatus(response?.data?.msg || '手动转账失败', 'error')
      } catch (error) {
        this.showStatus(error?.response?.data?.msg || '手动转账失败，请稍后重试', 'error')
      } finally {
        this.transferSubmitting = false
      }
    },
    async deleteItem(item) {
      if (!item || !item.id) {
        return
      }
      if (!window.confirm(`确认删除店铺“${item.shopName || item.username || ''}”的元数据吗？`)) {
        return
      }
      this.deletingId = item.id
      this.statusMessage = ''
      try {
        const response = await axios.delete(`${API_CONFIG.walletSource()}/${item.id}`, {
          headers: this.getRequestHeaders()
        })
        if (Number(response?.data?.code) === 200) {
          this.showStatus('钱包元数据删除成功')
          await this.fetchList()
          return
        }
        this.showStatus(response?.data?.msg || '钱包元数据删除失败', 'error')
      } catch (error) {
        this.showStatus(error?.response?.data?.msg || '钱包元数据删除失败，请稍后重试', 'error')
      } finally {
        this.deletingId = null
      }
    },
    goBack() {
      this.$router.push({
        path: '/wallet-management',
        query: this.cardNumber ? { cardNumber: this.cardNumber } : {}
      })
    }
  }
}
</script>

<style scoped>
.wallet-metadata-page {
  min-height: 100vh;
  height: 100vh;
  padding: 18px 20px 20px;
  background: linear-gradient(135deg, #10244b 0%, #1f4e8c 100%);
  color: #ffffff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-header {
  max-width: 1320px;
  width: 100%;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.page-title {
  margin: 0 0 8px;
  font-size: 30px;
  font-weight: 700;
}

.page-subtitle {
  margin: 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.primary-btn,
.ghost-btn,
.mini-btn,
.modal-close {
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.primary-btn,
.ghost-btn {
  min-width: 110px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 14px;
}

.primary-btn {
  background: linear-gradient(45deg, #667eea, #7b61ff);
  color: #ffffff;
}

.ghost-btn {
  background: rgba(255, 255, 255, 0.14);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.ghost-btn.light {
  background: #f3f4f6;
  color: #111827;
  border: 1px solid #d1d5db;
}

.primary-btn:disabled,
.ghost-btn:disabled,
.mini-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.status-banner {
  max-width: 1320px;
  width: 100%;
  margin: 0 auto 12px;
  border-radius: 14px;
  padding: 14px 16px;
}

.status-banner.success {
  background: rgba(46, 204, 113, 0.18);
  border: 1px solid rgba(46, 204, 113, 0.35);
}

.status-banner.error {
  background: rgba(231, 76, 60, 0.18);
  border: 1px solid rgba(231, 76, 60, 0.35);
}

.content-card {
  max-width: 1320px;
  width: 100%;
  margin: 0 auto;
  flex: 1;
  min-height: 0;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(10px);
  padding: 16px;
  box-sizing: border-box;
  overflow: hidden;
}

.placeholder-state {
  height: 100%;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.82);
  border: 1px dashed rgba(255, 255, 255, 0.24);
  border-radius: 14px;
}

.table-wrapper {
  height: 100%;
  overflow: auto;
}

.metadata-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 980px;
}

.metadata-table th,
.metadata-table td {
  padding: 14px 12px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 14px;
}

.metadata-table th {
  position: sticky;
  top: 0;
  background: rgba(13, 34, 68, 0.92);
  color: #dce7ff;
  font-weight: 600;
  z-index: 1;
}

.metadata-table td {
  color: #ffffff;
  word-break: break-all;
}

.action-column,
.table-actions {
  width: 170px;
}

.table-actions {
  display: flex;
  gap: 8px;
}

.mini-btn {
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
}

.edit-btn {
  background: rgba(102, 126, 234, 0.2);
  color: #dce7ff;
  border: 1px solid rgba(102, 126, 234, 0.35);
}

.delete-btn {
  background: rgba(255, 107, 107, 0.18);
  color: #ffb2b2;
  border: 1px solid rgba(255, 107, 107, 0.3);
}

.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.45);
}

.modal-card {
  width: 100%;
  max-width: 640px;
  border-radius: 18px;
  background: #ffffff;
  color: #1f2937;
  overflow: hidden;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.3);
}

.modal-header,
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
}

.modal-header {
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: #111827;
}

.modal-close {
  padding: 6px 12px;
  border-radius: 8px;
  background: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 20px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.form-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-item-full {
  grid-column: 1 / -1;
}

.form-item label {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.form-item input {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  outline: none;
  font-size: 14px;
  color: #111827;
}

.form-select {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  outline: none;
  font-size: 14px;
  color: #111827;
  background: #ffffff;
}

.form-item input::placeholder {
  color: #9ca3af;
}

.form-item input:focus,
.form-select:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.14);
}

.modal-footer {
  justify-content: flex-end;
  border-top: 1px solid #e5e7eb;
}

@media (max-width: 900px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-item-full {
    grid-column: auto;
  }
}

@media (max-width: 768px) {
  .wallet-metadata-page {
    padding: 12px;
  }

  .content-card {
    padding: 10px;
  }

  .header-actions {
    width: 100%;
  }

  .primary-btn,
  .ghost-btn {
    flex: 1;
  }
}
</style>
